import DriverForm from '@acorn-portal/shared/src/components/forms/driver/driver-form';
import DriverShortForm from '@acorn-portal/shared/src/components/forms/driver/driver-short-form';
import DLNumberDisclaimer from '@acorn-portal/shared/src/components/manage-drivers/dln-number-disclaimer';
import { DriverDetailsFormItem } from '@acorn-portal/shared/src/components/manage-drivers/types';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import JourneyCardLayout from '@acorn-portal/shared/src/layouts/journey-card-layout';
import { PcDriver } from '@acorn-portal/shared/src/services/subscriptionAPI';
import { setDriverData } from '@acorn-portal/shared/src/store/subscription-amend-drivers.slice';
import {
	DriverFormValues,
	getDriverDefaultValues,
} from '@acorn-portal/shared/src/utils/initialValues/get-driver-form-values';
import { DriverShortFormValues } from '@acorn-portal/shared/src/utils/initialValues/get-driver-short-form-values';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { driverSchema } from '@acorn-portal/shared/src/utils/validation/DriverSchema';
import { driverShortSchema } from '@acorn-portal/shared/src/utils/validation/DriverShortSchema';
import { Box } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import HelperModal from '@acorn-portal/shared/src/components/modal/HelperModal';
import ActionButtons from './components/action-buttons';
import AddButton from './components/add-button';
import DriverTable, { DriverTableData } from '@acorn-portal/shared/src/components/table/driver-table';
import TableWrapper from './components/table-wrapper';
import { usePageState } from './hooks/use-page-state';
import { useRemoveDriver } from './hooks/use-remove-driver';
import { useSubmitDriversPage } from './hooks/use-submit-drivers-page';
import { createOrUpdateNewDriver } from './utils/create-or-update-new-driver';
import { getAdditionalDetails } from './utils/get-additional-details';
import { getDriverTableData } from './utils/get-driver-table-data';
import { getDriverFormValues } from './utils/get-driver-form-values';
import { getDriverShortFormValues } from './utils/get-driver-short-form-values';
import { updateDriversFromShortForm } from './utils/update-drivers-from-short-form';
import { updateMainDrivers } from './utils/update-main-drivers';

const AmendDrivers = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	// Core data and functionality
	const workingData = useAppSelector((store) => store.subscriptionAmendDrivers.data); // modifiable driver data for the table and form components
	const driverLimit = 4;
	const { formStatus, activeDriverIndex, setActiveDriverIndex, showExistingDriverForm, showNewDriverForm, resetForm } =
		usePageState(driverLimit); // page state management
	const { showRemoveDriverModal, setShowRemoveDriverModal, onRemoveDriver } = useRemoveDriver(); // remove driver modal and action
	const { submitting, handleDriverPageSubmit } = useSubmitDriversPage(); // whole page submission; requoting with the working data
	const activeForm = activeDriverIndex !== null; // affecting visibility and disabling of multiple components
	// Baseline form data
	const activeDriverData = useMemo(() => {
		if (activeDriverIndex !== null && workingData) return workingData[activeDriverIndex];
		return null;
	}, [activeDriverIndex, workingData]);
	const driverDetails = useMemo(() => getAdditionalDetails(activeDriverData), [activeDriverData]);
	// Form specific data
	const shortFormValues = useMemo(() => getDriverShortFormValues(activeDriverData), [activeDriverData]); // initial values for short form (proposer)
	const fullFormValues = useMemo(() => getDriverFormValues(activeDriverData), [activeDriverData]); // initial values for the full form (additional)
	const proposerMaritalStatus = useMemo(
		() => workingData?.find((item) => item.isProposer)?.maritalStatus || '',
		[workingData],
	);
	// DriverTable data and modifiers
	const tableData: DriverTableData = useMemo(() => getDriverTableData(workingData || []), [workingData]);

	const updateMainDriver = (index: number) => {
		const newDrivers = updateMainDrivers(workingData, index);
		dispatch(setDriverData(newDrivers));
	};

	// Submit handler for both forms - updating the working data in the store only
	const handleFormSubmit = <T extends DriverFormValues | DriverShortFormValues>(
		data: T,
		transform: (formValues: T, driverIndex: number, driverData: PcDriver[]) => PcDriver[],
	) => {
		if (activeDriverIndex === null || !workingData) return;
		const newDrivers = transform(data, activeDriverIndex, workingData);
		dispatch(setDriverData(newDrivers));
		resetForm();
	};

	// Handle no drivers
	useEffect(() => {
		if (!workingData || workingData.length === 0) {
			navigate(ROUTE_PATHS.error);
		}
	}, [workingData]);

	return (
		<JourneyCardLayout pageTitle='Manage Drivers' loading={submitting}>
			<Box sx={{ marginBottom: '1em' }}>
				{tableData && tableData.length > 0 && (
					<TableWrapper>
						<DriverTable
							data={tableData}
							disabled={activeForm}
							onEditClick={showExistingDriverForm}
							onRemoveClick={(index) => {
								setActiveDriverIndex(index);
								setShowRemoveDriverModal(true);
							}}
							onMainClick={updateMainDriver}
						/>
						{!activeForm && (
							<AddButton
								variant='contained'
								onClick={showNewDriverForm}
								disabled={activeForm || workingData.length >= driverLimit}
							>
								ADD ANOTHER DRIVER
							</AddButton>
						)}
					</TableWrapper>
				)}
				{activeForm && (
					<>
						{formStatus === 'editing-proposer' && shortFormValues && (
							<DriverShortForm
								driverDetails={driverDetails as DriverDetailsFormItem}
								initialValues={shortFormValues}
								onCancel={resetForm}
								onSubmit={(data: DriverShortFormValues) => handleFormSubmit(data, updateDriversFromShortForm)}
								schema={driverShortSchema}
							/>
						)}
						{(formStatus === 'creating-additional' || formStatus === 'editing-additional') && (
							<>
								<DLNumberDisclaimer />
								<DriverForm
									initialValues={formStatus === 'editing-additional' ? fullFormValues : getDriverDefaultValues()}
									submitText={formStatus === 'editing-additional' ? 'Edit' : 'Add'}
									onCancel={resetForm}
									onSubmit={(data) => handleFormSubmit(data, createOrUpdateNewDriver)}
									schema={driverSchema(proposerMaritalStatus)}
									motoringClaimsYears={5}
								/>
							</>
						)}
					</>
				)}
			</Box>
			{showRemoveDriverModal && (
				<HelperModal
					open={showRemoveDriverModal}
					onClose={() => setShowRemoveDriverModal(false)}
					title='Remove Driver'
					description='Are you sure?'
					closeText='Cancel'
					submitText='Delete'
					onSubmit={() => {
						onRemoveDriver(activeDriverIndex || 0);
						setActiveDriverIndex(null);
					}}
				/>
			)}
			<ActionButtons disableSubmit={activeForm} handleSubmit={handleDriverPageSubmit} />
		</JourneyCardLayout>
	);
};

export default AmendDrivers;
